import { createContext, useContext, useEffect, useRef, useState } from 'react'
import { getLocalStorageItem } from 'utils/loadData'
import { useAuth } from 'contexts/AuthContext'

const FiltersContext = createContext()

export const useFilters = () => useContext(FiltersContext)

// Estado por defecto de los filtros de la vista "Comprar". Al iniciar sesión se
// restablece a este valor para que quede seleccionado "Activos en cronograma
// actual".
const defaultComprarFilters = {
  cronograma_actual: true,
  operación_directa: false,
  marcas_líderes: false,
  todos_provedores: false,
  directa_y_redistribucion: true,
}

const useLocalStorageState = (key, defaultValue) => {
  const [state, setState] = useState(() =>
    getLocalStorageItem(key, defaultValue),
  )

  const setLocalStorageState = (newState) => {
    localStorage.setItem(key, JSON.stringify(newState))
    setState(newState)
  }

  return [state, setLocalStorageState]
}

export const FiltersProvider = ({ children }) => {
  const defaultPedidosFilters = {
    todos: true,
    cronograma_actual: false,
    este_mes: false,
    mes_pasado: false,
    historico: true,
    todos_provedores: true,
    operación_directa: false,
    marcas_líderes: false,
    todos_estados: true,
    aprobado: false,
    facturado: false,
    en_preparacion: false,
    generado: false,
    confeccion: false,
    desaprobado: false,
    cancelado: false,
    no_consolidado: false,
    periodo: {
      fecha_inicio: '',
      fecha_fin: '',
    },
  }

  const [comprarFilters, setComprarFilters] = useLocalStorageState(
    'comprarFilters',
    defaultComprarFilters,
  )

  // La primera vez que el operador entra al portal en una sesión nueva se
  // selecciona "Activos en cronograma actual". Esto cubre dos casos:
  //   1. El login explícito (transición de "sin token" a "con token").
  //   2. Abrir el portal en una pestaña/sesión nueva con el token ya
  //      persistido en localStorage (en ese caso no hay transición de token,
  //      por eso se detecta el inicio de sesión con sessionStorage).
  // En recargas o al navegar entre páginas dentro de la misma sesión se
  // mantiene la selección previa del usuario (sessionStorage sobrevive a las
  // recargas, pero se limpia al cerrar la pestaña/navegador).
  const SESSION_INIT_KEY = 'comprarFiltersSessionInit'
  const { token } = useAuth()
  const prevTokenRef = useRef(token)
  useEffect(() => {
    const prevToken = prevTokenRef.current
    prevTokenRef.current = token

    if (!token) {
      // Al cerrar sesión se reinicia el marcador para que el próximo ingreso
      // vuelva a restablecer los filtros por defecto.
      sessionStorage.removeItem(SESSION_INIT_KEY)
      return
    }

    const isLoginTransition = !prevToken && token
    const isNewSession = !sessionStorage.getItem(SESSION_INIT_KEY)

    if (isLoginTransition || isNewSession) {
      sessionStorage.setItem(SESSION_INIT_KEY, '1')
      setComprarFilters({ ...defaultComprarFilters })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token])

  const [pedidosFilters, setPedidosFilters] = useLocalStorageState(
    'pedidosFilters',
    defaultPedidosFilters,
  )

  const onPedidosFiltersChange = (newFilters) => {
    setPedidosFilters((prevFilter) => {
      const filtersChanged =
        JSON.stringify(prevFilter) !== JSON.stringify(newFilters)
      if (filtersChanged) {
        localStorage.setItem('pedidosPageNumber', JSON.stringify(1))
      }
      return newFilters
    })
  }

  const resetPedidosFilters = () => {
    setPedidosFilters(defaultPedidosFilters)
  }

  const contextValues = {
    comprarFilters,
    setComprarFilters,
    onComprarFiltersChange: setComprarFilters,
    pedidosFilters,
    onPedidosFiltersChange,
    resetPedidosFilters,
  }

  return (
    <FiltersContext.Provider value={contextValues}>
      {children}
    </FiltersContext.Provider>
  )
}
